import type { AnnotatedJsonSchema } from '@web-config-app/core';
import { getSchemaFromEntries } from '../get-schema-from-entries/get-schema-from-entries';

/**
 * Alters a schema of type `object` by only including properties for which the passed
 * `filterProperties` function returns true and performs the mutateProperties function on each property schema.
 *
 * @param schema current schema
 * @param filterProperties a function to filter stuff out
 * @param mutateProperties a function that does some mutation on the current property schema
 * @param mutateRequiredArray a function that mutates the required array of the parent schema
 *
 * @return the altered properties schema after running the
 */

export interface GetSchemaWithMutatedPropertiesArgs {
  schema: AnnotatedJsonSchema;
  filterProperties?: (
    schema: AnnotatedJsonSchema,
    propertyName?: string,
  ) => boolean;
  mutateProperties?: (
    schema: Partial<AnnotatedJsonSchema>,
    propertyName?: string,
  ) => AnnotatedJsonSchema;
  mutateRequiredArray?: (
    schema: Partial<AnnotatedJsonSchema>,
    propertyName?: string,
  ) => boolean | undefined;
}
export const getSchemaWithMutatedProperties = ({
  schema,
  filterProperties = () => true,
  mutateProperties,
  mutateRequiredArray,
}: GetSchemaWithMutatedPropertiesArgs) => {
  if (schema.type !== 'object') {
    return schema;
  }
  const requiredArray: string[] = [];
  const notRequiredArray: string[] = [];

  const schemaEntries = Object.entries(schema.properties ?? {});
  const recalculatedProperties = getSchemaFromEntries(
    schemaEntries
      .filter(([propertyName, propertySchema]) =>
        filterProperties(propertySchema, propertyName),
      )
      .map(([propertyName, propertySchema]) => {
        const mutatedSchema =
          mutateProperties?.(propertySchema, propertyName) ?? propertySchema;
        const isSchemaRequired = mutateRequiredArray?.(propertySchema);
        if (isSchemaRequired !== undefined) {
          (isSchemaRequired ? requiredArray : notRequiredArray).push(
            propertyName,
          );
        }
        return [propertyName, mutatedSchema] as [string, AnnotatedJsonSchema];
      }),
  );

  const updatedSchema = {
    ...schema,
    properties: recalculatedProperties,
  };

  // If either array is empty, we don't need to recalculate the required array
  // because the schema is already correct, otherwise we need to recalculate it
  // by merging the schemas required array with the current calculated required array and removing
  // the not required array from it
  if (requiredArray.length > 0 || notRequiredArray.length > 0) {
    updatedSchema.required = [
      ...new Set(
        [...(schema?.required ?? []), ...requiredArray].filter(
          (element) => !notRequiredArray.includes(element),
        ),
      ),
    ];
  }

  return updatedSchema as AnnotatedJsonSchema;
};
